'use client'

import { Cell, Image, Text } from '@vinted/web-ui'
import { useIntl } from 'react-intl'
import { PropsWithChildren } from 'react'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { CurrencyAmountModel, formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { TransactionSide } from '@marketplace-web/messaging/conversation-messaging-data'
import {
  conversationItemPhotoClick,
  conversationMultipleItemsPhotosClick,
} from '@marketplace-web/messaging/tracking-data'
import { ShipmentStatus } from '@marketplace-web/conversation-shipping/conversation-shipping-data'

import HeroMessageUserProtection from './HeroMessageUserProtection'

enum ItemThumbnailSize {
  Small = 'thumb70x100',
  Medium = 'thumb150x210',
  Large = 'thumb310x430',
  XLarge = 'thumb364x428',
  X2Large = 'thumb624x428',
}

type ItemPhotoModel = {
  url: string
  imageNo: number
  isMain: boolean
  orientation?: number | null
  thumbnails: Array<{
    type: ItemThumbnailSize | string
    url: string
  }>
}

const getPhotoThumbnail = (photo: ItemPhotoModel, size: ItemThumbnailSize) => {
  const thumbnails = photo.thumbnails || []
  const thumbnail = thumbnails.find(({ type }) => type === size)

  if (!thumbnail) return null

  return thumbnail.url
}

type Props = {
  conversationId: string
  isSellerBusiness: boolean
  itemCount: number
  itemId: number
  itemUrl?: string
  oppositeUserId?: string
  photo?: ItemPhotoModel
  price: CurrencyAmountModel
  sellerId?: number
  serviceFee: CurrencyAmountModel
  shipmentStatus?: ShipmentStatus
  itemTitle: string
  toggleSidebar?: () => void
  totalAmountWithoutTax: CurrencyAmountModel
  transactionId: number
  transactionStatus?: number
  userSide: string | null
}

const HeroMessage = ({
  children,
  conversationId,
  itemCount,
  oppositeUserId,
  photo,
  price,
  itemTitle,
  toggleSidebar,
  transactionId,
  itemUrl,
  userSide,
  ...heroMessageUserProtectionProps
}: PropsWithChildren<Props>) => {
  const { track } = useTracking()

  const breakpoints = useBreakpoint()
  const { locale } = useIntl()

  const handleSingleItemPhotoClick = () => {
    track(conversationItemPhotoClick({ transactionId, conversationId, userSide, oppositeUserId }))
  }

  const handleMultipleItemPhotoClick = () => {
    track(
      conversationMultipleItemsPhotosClick({
        transactionId,
        conversationId,
        userSide,
        oppositeUserId,
      }),
    )

    toggleSidebar?.()
  }

  const wrapComponentWithUrl = (component: JSX.Element) => {
    if (!itemUrl) return component

    return (
      <Cell
        styling="tight"
        url={itemCount === 1 ? itemUrl : undefined}
        onClick={itemCount > 1 ? handleMultipleItemPhotoClick : handleSingleItemPhotoClick}
        clickable
        aria={{ 'aria-label': itemCount > 1 ? itemTitle : undefined }}
      >
        {component}
      </Cell>
    )
  }

  const renderPhotoLabel = () => {
    return itemCount > 1 ? itemCount.toString() : undefined
  }

  const renderPhoto = () => {
    if (!photo) return null

    const thumbnailUrl = getPhotoThumbnail(photo, ItemThumbnailSize.Medium)

    const image = (
      <Image
        src={thumbnailUrl}
        styling="rounded"
        size="large"
        scaling="cover"
        label={renderPhotoLabel()}
        testId="hero-message-item-image"
      />
    )

    return wrapComponentWithUrl(image)
  }

  const renderTitle = () => {
    const text = <Text as="h2" text={itemTitle} type="title" />

    return wrapComponentWithUrl(text)
  }

  const renderBody = () => {
    if (!price || !price.currencyCode) return null

    const formattedPrice = formatCurrencyAmount(price, locale)

    return (
      <>
        <Text
          type="subtitle"
          text={formattedPrice}
          testId="hero-message-price"
          theme="muted"
          as="p"
        />
        <HeroMessageUserProtection
          {...heroMessageUserProtectionProps}
          isCorrectUserSide={userSide === TransactionSide.Seller}
          itemCount={itemCount}
          price={price}
          thumbnailUrl={photo ? getPhotoThumbnail(photo, ItemThumbnailSize.Medium) : undefined}
          title={itemTitle}
          transactionId={transactionId}
        />
      </>
    )
  }

  return (
    <>
      <Cell
        prefix={renderPhoto()}
        title={renderTitle()}
        body={renderBody()}
        suffix={breakpoints.tabletsUp && children}
        testId="hero-message"
      />
      {breakpoints.phones && (
        <div className="u-ui-padding-horizontal-large u-ui-padding-bottom-large">{children}</div>
      )}
    </>
  )
}

export default HeroMessage
